import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  Plus, 
  Minus,
  Star,
  Home as HomeIcon,
  Building2,
  Calendar
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNotification } from '../context/NotificationContext';

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;
}

function Services() {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data, error } = await supabase
          .from('services')
          .select('*')
          .order('price');

        if (error) throw error;
        setServices(data || []);
      } catch (error) {
        console.error('Error fetching services:', error);
        showNotification('error', 'Failed to load services. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [showNotification]);

  const faqs = [
    {
      question: "What's included in your routine cleaning service?",
      answer: "Our routine cleaning service includes dusting, vacuuming, mopping, bathroom cleaning, kitchen cleaning, and making beds. We focus on high-traffic areas and ensure all visible surfaces are clean and sanitized."
    },
    {
      question: "How long does a typical cleaning session take?",
      answer: "A typical routine cleaning session takes 2-4 hours depending on the size of your home. Deep cleaning and move-in/move-out services take longer to ensure thorough attention to detail."
    },
    {
      question: "Do I need to provide cleaning supplies?",
      answer: "No, we bring all necessary cleaning supplies and equipment. However, if you prefer specific products to be used in your home, please let us know in advance."
    },
    {
      question: "Are your cleaning products eco-friendly?",
      answer: "Yes, we use environmentally friendly cleaning products that are effective yet safe for your family and pets. We can also accommodate specific product requests if you have preferences."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Thompson",
      role: "Homeowner",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80",
      content: "The deep cleaning service was exceptional. They paid attention to every detail and transformed my home.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Business Owner",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80",
      content: "Regular cleaning service for my office has been reliable and thorough. Highly recommended!",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Apartment Resident",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80",
      content: "Move-out cleaning was perfect. Got my full deposit back thanks to their detailed work.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-[#f0efe9] dark:bg-gray-900">
      {/* Hero Section */}
      <div className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-light text-gray-800 dark:text-white mb-6">Our Services</h1>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              We offer professional cleaning services tailored to your needs
            </p>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <section className="py-20 bg-[#f0efe9] dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {services.map((service) => (
              <div key={service.id} className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 flex flex-col">
                <div className="mb-6">
                  {service.name.includes('Regular') || service.name.includes('Routine') ? (
                    <HomeIcon className="w-10 h-10 text-gray-800 dark:text-gray-200" />
                  ) : service.name.includes('Deep') ? (
                    <Building2 className="w-10 h-10 text-gray-800 dark:text-gray-200" />
                  ) : (
                    <Calendar className="w-10 h-10 text-gray-800 dark:text-gray-200" />
                  )}
                </div>
                <h3 className="text-xl font-medium text-gray-800 dark:text-white mb-2">{service.name}</h3>
                <div className="w-12 h-0.5 bg-gray-400 dark:bg-gray-600 mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  {service.description}
                </p>
                <div className="mt-auto">
                  <p className="text-2xl font-light text-gray-800 dark:text-white mb-6">
                    ${(service.price / 100).toFixed(2)}
                  </p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-300 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 dark:text-gray-300">{service.duration} minutes service</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-300 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 dark:text-gray-300">Professional equipment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-gray-800 dark:text-gray-300 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 dark:text-gray-300">Satisfaction guaranteed</span>
                    </li>
                  </ul>
                  <Link 
                    to="/book" 
                    className="inline-flex items-center text-gray-800 dark:text-gray-300 hover:text-black dark:hover:text-white"
                  >
                    Book Now
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-light text-gray-800 dark:text-white mb-4">Client Testimonials</h2>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-500 fill-current' : 'text-gray-300 dark:text-gray-600'}`} 
                    />
                  ))}
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-6 italic">
                  "{testimonial.content}"
                </p>
                <p className="font-medium text-gray-800 dark:text-white">{testimonial.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{testimonial.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#f0efe9] dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-light text-gray-800 dark:text-white mb-4">Frequently Asked Questions</h2>
            <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-medium text-gray-800 dark:text-white">{faq.question}</span>
                  {expandedFaq === index ? (
                    <Minus className="w-5 h-5 text-gray-800 dark:text-gray-300" />
                  ) : (
                    <Plus className="w-5 h-5 text-gray-800 dark:text-gray-300" />
                  )}
                </button>
                <div 
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    expandedFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="px-6 pb-6 text-gray-600 dark:text-gray-300">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-800 dark:bg-gray-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-light mb-6">Ready for a Spotless Space?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Book your cleaning service today and enjoy a pristine environment. Satisfaction guaranteed!
          </p>
          <Link
            to="/book"
            className="inline-block px-8 py-3 border-2 border-white hover:bg-white hover:text-gray-800 transition-colors"
          >
            Book Now
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Services;